import { useEffect, useState } from "react";
import { http, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DownloadSimple, ArrowClockwise, Cpu, User } from "@phosphor-icons/react";

const cat = { radius: "text-primary", coa: "text-[hsl(var(--warning))]", reminder: "text-[hsl(var(--success))]", cron: "text-muted-foreground" };

const SCOPES = [
  { key: "server", label: "Server log", icon: Cpu, kinds: ["all", "cron", "coa", "reminder"], desc: "Backend cron jobs, CoA disconnects, and outbound reminders — what the server itself did." },
  { key: "user",   label: "User log",   icon: User, kinds: ["all", "radius"], desc: "Per-subscriber RADIUS auth + accounting stream. Filter by username in the search box." },
];

export default function Logs() {
  const [scope, setScope] = useState("server");
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "500", scope });
    if (kind !== "all") params.set("kind", kind);
    const { data } = await http.get(`/logs?${params.toString()}`);
    setLogs(data);
    setLoading(false);
  };
  useEffect(() => { setKind("all"); }, [scope]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, kind]);

  const exportCsv = async () => {
    const { data } = await http.get("/logs/export.csv", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = `cts-logs-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const active = SCOPES.find((s) => s.key === scope);
  const filtered = q ? logs.filter((l) => JSON.stringify(l).toLowerCase().includes(q.toLowerCase())) : logs;

  return (
    <div className="space-y-6 fade-up" data-testid="logs-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">CTS Central Log Server</div>
          <h1 className="font-display font-bold text-4xl mt-1">Event stream</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{active?.desc}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={load} className="rounded-sm border-border" data-testid="logs-refresh"><ArrowClockwise size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
          <Button onClick={exportCsv} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="logs-csv"><DownloadSimple size={14} className="mr-2" /> Export CSV</Button>
        </div>
      </div>

      {/* Scope tabs */}
      <div className="flex gap-2">
        {SCOPES.map((s) => (
          <button key={s.key} onClick={() => setScope(s.key)} data-testid={`logs-scope-${s.key}`}
            className={`inline-flex items-center gap-2 px-4 py-2 border rounded-sm text-xs uppercase font-mono transition-colors ${scope === s.key ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
            <s.icon size={14} /> {s.label}
          </button>
        ))}
      </div>

      {/* Kind + search */}
      <div className="flex items-center gap-2 flex-wrap">
        {active?.kinds.map((k) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`logs-kind-${k}`} className={`text-xs uppercase tracking-wide px-3 py-1.5 border rounded-sm transition-colors ${kind === k ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>{k}</button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="ml-auto h-8 bg-secondary border border-border px-3 text-xs font-mono rounded-sm min-w-[220px]" data-testid="logs-search" />
      </div>

      <div className="border border-border bg-card">
        <div className="max-h-[680px] overflow-y-auto font-mono text-xs" data-testid="logs-body">
          {filtered.map((l, i) => (
            <div key={i} className="border-b border-border/40 py-1.5 px-3 flex gap-3 hover:bg-accent/20">
              <span className="text-muted-foreground shrink-0 w-36">{fmtDate(l.ts)}</span>
              <span className={`shrink-0 w-20 uppercase ${cat[l.category] || "text-foreground"}`}>{l.category}</span>
              <span className="shrink-0 w-40 truncate">{l.event}</span>
              <span className="shrink-0 w-28 text-muted-foreground truncate">{l.username || "—"}</span>
              <span className="truncate">{l.message || l.nas_name || ""}</span>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center py-16 text-muted-foreground text-sm">No log entries.</div>}
        </div>
      </div>
    </div>
  );
}
